import { useState } from 'react';
import { Box, Button } from '@mui/material';
import { Hub as HubIcon } from '@mui/icons-material';
import { pageContainer, pageTitleProps } from '../../styles/pageStyles';
import { slate } from '../../constants/uiTokens';

import WhatsAppHero from '../integrations/WhatsAppHero';
import WhatsAppConfig from '../integrations/WhatsAppConfig';
import MessageHistoryCard from '../integrations/MessageHistoryCard';
import CreditChip from '../integrations/CreditChip';
import BalanceBanner from '../integrations/BalanceBanner';
import Txt from '../integrations/Txt';

/**
 * DesignLabPage — SANDBOX de design (rota dev-only `/design-lab`, SEM auth e SEM API).
 *
 * Renderiza os componentes APRESENTACIONAIS reais da /integrations com dados de mentira,
 * num switcher de cenários. Serve para:
 *  - dar à designer uma "versão estática" fiel do que existe (referência p/ redesenhar);
 *  - permitir conferir o visual (inclusive eu, via screenshot) sem precisar de login/API.
 *
 * Importante: o que está aqui é EXATAMENTE o que roda em produção (mesmos componentes),
 * só que alimentado por mock — então o que ela ajustar no apresentacional vale 1:1.
 */

const MOCK_MODELS = [
  {
    id: 1,
    name: 'Formal',
    previewCreated: 'Olá, Maria! Seu agendamento foi confirmado.\n\n🗓️ Limpeza + Avaliação\n📅 quarta, 02/07 às 14:30\n📍 Unidade Centro',
    previewCreatedWithNotes: 'Olá, Maria! Seu agendamento foi confirmado.\n\n🗓️ Limpeza + Avaliação\n📅 quarta, 02/07 às 14:30\n📍 Unidade Centro\n\n{{ORIENTACOES}}',
  },
  {
    id: 2,
    name: 'Descontraído',
    previewCreated: 'Oi, Maria! 😊 Deu tudo certo, seu horário está confirmado!\n\n✨ Limpeza + Avaliação\n📅 quarta, 02/07 às 14:30\n📍 Unidade Centro',
    previewCreatedWithNotes: 'Oi, Maria! 😊 Deu tudo certo, seu horário está confirmado!\n\n✨ Limpeza + Avaliação\n📅 quarta, 02/07 às 14:30\n📍 Unidade Centro\n\n{{ORIENTACOES}}',
  },
];

const MOCK_MESSAGES = [
  { createdAt: '2026-07-02T14:32:00', direction: 'outbound', purpose: 'confirmation_request', status: 'read', phoneMasked: '(31) 9••••-1234' },
  { createdAt: '2026-07-02T14:31:00', direction: 'outbound', purpose: 'reminder', status: 'delivered', phoneMasked: '(31) 9••••-8890' },
  { createdAt: '2026-07-02T13:10:00', direction: 'inbound', purpose: 'reply', status: 'received', phoneMasked: '(31) 9••••-4521' },
  { createdAt: '2026-07-01T18:05:00', direction: 'outbound', purpose: 'reminder', status: 'failed', phoneMasked: '(31) 9••••-2210' },
];
const MOCK_SUMMARY = { sent: 128, received: 12, failedNoCredit: 7 };

const BASE_FORM = {
  enabled: true,
  templateStyle: 2,
  messageNotes: 'Inclua o endereço da unidade e ofereça reagendamento caso o cliente não possa comparecer.',
  notifyOnPublicCreate: true,
  notifyOnPrivateCreate: true,
  reminderEnabled: true,
  reminderHoursBefore: 24,
  reminderRespectBusinessHours: true,
  reminderBusinessStart: 8,
  reminderBusinessEnd: 18,
  ownerNotifyEnabled: true,
  ownerPhone: '(31) 99876-5432',
  ownerNotifyOnCreate: true,
  ownerNotifyOnConfirm: true,
  ownerNotifyOnCancel: false,
};

const SCENARIOS = [
  ['ativado', 'Ativado'],
  ['desativado', 'Desativado'],
  ['saldoBaixo', 'Saldo baixo'],
  ['saldoZero', 'Saldo zero'],
  ['historicoVazio', 'Hist. vazio'],
];

function buildPreview(model, notes) {
  const trimmed = notes?.trim();
  if (!model) return '';
  return trimmed
    ? (model.previewCreatedWithNotes || '').replace('{{ORIENTACOES}}', trimmed)
    : (model.previewCreated || '');
}

export default function DesignLabPage() {
  const urlScenario = new URLSearchParams(window.location.search).get('scenario');
  const [scenario, setScenario] = useState(
    SCENARIOS.some(([k]) => k === urlScenario) ? urlScenario : 'ativado',
  );
  const [form, setForm] = useState(BASE_FORM);

  const updateField = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const enabled = scenario !== 'desativado';
  const balance = scenario === 'saldoZero' ? 0 : scenario === 'saldoBaixo' ? 42 : 1240;
  const balanceState = balance === 0 ? 'zero' : balance <= 80 ? 'low' : 'ok';
  const emptyHistory = scenario === 'historicoVazio';

  const selectedModel = MOCK_MODELS.find((m) => m.id === form.templateStyle);
  const previewText = buildPreview(selectedModel, form.messageNotes);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 3 }}>
      {/* Barra de cenários (igual ao demo bar do mockup) */}
      <Box sx={{ position: 'sticky', top: 0, zIndex: 50, mb: 3, mx: 'auto', maxWidth: 1440, px: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', bgcolor: '#10233b', color: '#fff', p: 1, borderRadius: '12px', boxShadow: '0 8px 28px rgba(8,18,33,.35)' }}>
          <Txt component="span" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase', color: '#7e93ad', px: 1 }}>
            Design Lab · cenário
          </Txt>
          {SCENARIOS.map(([key, label]) => (
            <Button
              key={key}
              onClick={() => setScenario(key)}
              disableElevation
              sx={{
                minWidth: 0, px: 1.5, py: 0.5, fontSize: 12, fontWeight: 600, borderRadius: '7px',
                color: scenario === key ? '#fff' : '#9fb2c9',
                bgcolor: scenario === key ? 'primary.main' : 'transparent',
                '&:hover': { bgcolor: scenario === key ? 'primary.main' : 'rgba(255,255,255,.08)' },
              }}
            >
              {label}
            </Button>
          ))}
          <Txt component="span" sx={{ fontSize: 11, color: '#7e93ad', ml: 'auto', px: 1 }}>
            Para ver mobile, reduza a janela do navegador.
          </Txt>
        </Box>
      </Box>

      {/* Página /integrations renderizada com mock */}
      <Box sx={{ ...pageContainer, px: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
          <Box>
            <Txt component="h1" sx={{ ...pageTitleProps, fontSize: 24, display: 'flex', alignItems: 'center', gap: 1 }}>
              <HubIcon sx={{ fontSize: 32, color: 'primary.dark' }} /> Integrações
            </Txt>
            <Txt sx={{ fontSize: 13.5, color: slate[600], mt: 0.5 }}>
              Conecte canais de comunicação para automatizar o contato com seus clientes.
            </Txt>
          </Box>
          {enabled && <CreditChip balance={balance} state={balanceState} onBuy={() => {}} />}
        </Box>

        {!enabled ? (
          <WhatsAppHero
            previewText={previewText}
            previewShowButtons={false}
            onActivate={() => setScenario('ativado')}
            activating={false}
          />
        ) : (
          <>
            {(balanceState === 'zero' || balanceState === 'low') && (
              <BalanceBanner state={balanceState} balance={balance} onBuy={() => {}} sx={{ mb: 2 }} />
            )}
            <WhatsAppConfig
              form={form}
              updateField={updateField}
              models={MOCK_MODELS}
              onDisable={() => setScenario('desativado')}
              togglingEnabled={false}
              previewText={previewText}
              previewShowButtons={form.reminderEnabled}
              previewEmpty={!previewText?.trim()}
            />
            <Box sx={{ mt: 2 }}>
              <MessageHistoryCard
                messages={emptyHistory ? [] : MOCK_MESSAGES}
                total={emptyHistory ? 0 : 67}
                page={0}
                rowsPerPage={20}
                onPageChange={() => {}}
                onRowsPerPageChange={() => {}}
                loading={false}
                error={null}
                setError={() => {}}
                summary={emptyHistory ? null : MOCK_SUMMARY}
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
